import { useParams } from '@tanstack/react-router'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { clientsApi, tmApi } from '@/lib/api'
import { useAuthStore } from '@/store/auth'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { TerminologyManager } from '../components/TerminologyManager'
import { TermCandidateReviewPanel } from '../components/TermCandidateReviewPanel'
import { FeedbackList } from '../components/FeedbackList'
import { RagEditor } from '../components/RagEditor'
import { TMManager } from '../components/TMManager'
import { ChevronLeft, Database, BookOpen, MessageSquare, FileText, Pencil, Check, X } from 'lucide-react'
import { Link } from '@tanstack/react-router'
import { Button } from '@/components/ui/button'
import { Switch } from '@/components/ui/switch'
import { toast } from 'sonner'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { countText } from '@/lib/utils'
import { useState } from 'react'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'

export function ClientDetail() {
  const { clientId } = useParams({ from: '/clients/$clientId' })
  const queryClient = useQueryClient()
  const { user } = useAuthStore()
  // 术语审核仅 founder/pm 可访问（后端 requireRole(['founder','pm'])）
  // 译者点飞书通知链接过来也看不到该 Tab，符合「译者点链接无效」的预期
  const canReviewTerms = user?.role === 'founder' || user?.role === 'pm'
  
  const { data: response, isLoading } = useQuery({
    queryKey: ['client', clientId],
    queryFn: () => clientsApi.getById(clientId)
  })

  const client = (response as any)?.data

  // TM 统计（仅 TM 开启时查询，用于顶部 TM 卡片显示条数）
  const { data: tmStatsResponse } = useQuery({
    queryKey: ['tm-stats', clientId],
    queryFn: () => tmApi.getStats(clientId),
    enabled: !!client?.enableTM,
  })
  const tmTotal = tmStatsResponse?.data?.total ?? 0

  const [isEditing, setIsEditing] = useState(false)
  const [editCode, setEditCode] = useState('')
  const [editBillingType, setEditBillingType] = useState<'source' | 'target'>('source')

  const toggleTMMutation = useMutation({
    mutationFn: (enableTM: boolean) => clientsApi.toggleTM(clientId, enableTM),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['client', clientId] })
      toast.success('TM 设置已更新')
    },
    onError: (error: any) => {
      toast.error(`更新失败: ${error.message || '未知错误'}`)
    },
  })

  const updateClientMutation = useMutation({
    mutationFn: (data: { code?: string; billingType?: 'source' | 'target' }) => 
      clientsApi.update(clientId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['client', clientId] })
      toast.success('客户信息已更新')
      setIsEditing(false)
    },
    onError: (error: any) => {
      toast.error(`更新失败: ${error.message || '未知错误'}`)
    },
  })

  const handleToggleTM = (checked: boolean) => {
    toggleTMMutation.mutate(checked)
  }

  const startEditing = () => {
    if (client) {
      setEditCode(client.code || '')
      setEditBillingType(client.billingType || 'source')
      setIsEditing(true)
    }
  }

  const cancelEditing = () => {
    setIsEditing(false)
  }

  const saveEditing = () => {
    updateClientMutation.mutate({
      code: editCode,
      billingType: editBillingType,
    })
  }

  if (isLoading) return <div className="p-8 text-center">加载中...</div>
  if (!client) return <div className="p-8 text-center text-destructive">未找到该客户</div>

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" asChild>
            <Link to="/clients" aria-label="返回客户列表">
              <ChevronLeft className="h-4 w-4" />
            </Link>
          </Button>
          <div>
            <h1 className="text-2xl font-bold tracking-tight">{client.name}</h1>
            <div className="flex items-center gap-2 text-muted-foreground text-xs">
              {isEditing ? (
                <>
                  <span>客户代码:</span>
                  <Input
                    value={editCode}
                    onChange={(e) => setEditCode(e.target.value)}
                    className="h-6 w-24 text-xs"
                  />
                  <span className="ml-2">计费方式:</span>
                  <Select value={editBillingType} onValueChange={(v) => setEditBillingType(v as 'source' | 'target')}>
                    <SelectTrigger className="h-6 w-28 text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="source">按源文字数</SelectItem>
                      <SelectItem value="target">按目标文字数</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6"
                    onClick={saveEditing}
                    disabled={updateClientMutation.isPending}
                    aria-label="保存客户信息"
                  >
                    <Check className="h-3 w-3 text-success" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6"
                    onClick={cancelEditing}
                    aria-label="取消编辑"
                  >
                    <X className="h-3 w-3 text-destructive" />
                  </Button>
                </>
              ) : (
                <>
                  <span>客户代码: {client.code} · 计费方式: {client.billingType === 'source' ? '按源文字数' : '按目标文字数'}</span>
                  <Button variant="ghost" size="icon" className="h-5 w-5" onClick={startEditing} aria-label="编辑客户信息">
                    <Pencil className="h-3 w-3" />
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">翻译记忆库</CardTitle>
            <Database className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <span className={client.enableTM ? "text-success font-medium" : "text-muted-foreground"}>
                {client.enableTM ? `${tmTotal} 条` : '未启用'}
              </span>
              <Switch
                checked={client.enableTM || false}
                onCheckedChange={handleToggleTM}
                disabled={toggleTMMutation.isPending}
              />
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              {client.enableTM
                ? '可导入双语 xlsx 或自动存入完成项目译文'
                : '启用后可复用历史译文'}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">RAG 知识库</CardTitle>
            <FileText className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {(() => {
                if (!client.ragMarkdown) return '未配置';
                const stats = countText(client.ragMarkdown);
                return `${stats.count} ${stats.unit}`;
              })()}
            </div>
            <p className="text-xs text-muted-foreground">
              客户专属翻译指南和偏好
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">术语库</CardTitle>
            <BookOpen className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">-</div>
            <p className="text-xs text-muted-foreground">
              术语词条与待审核候选
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">客户反馈</CardTitle>
            <MessageSquare className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">-</div>
            <p className="text-xs text-muted-foreground">
              历史反馈和修订规律
            </p>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="rag" className="space-y-4">
        <TabsList className={`grid w-full max-w-lg ${canReviewTerms ? 'grid-cols-4' : 'grid-cols-3'}`}>
          <TabsTrigger value="rag">RAG 知识库</TabsTrigger>
          <TabsTrigger value="terms">术语管理</TabsTrigger>
          <TabsTrigger value="tm">TM 记忆库</TabsTrigger>
          <TabsTrigger value="feedback">客户反馈</TabsTrigger>
        </TabsList>

        <TabsContent value="rag" className="mt-4">
          <RagEditor client={client} />
        </TabsContent>

        <TabsContent value="terms" className="mt-4">
          {canReviewTerms ? (
            <Tabs defaultValue="library">
              <TabsList>
                <TabsTrigger value="library">术语库</TabsTrigger>
                <TabsTrigger value="review">待审核</TabsTrigger>
              </TabsList>
              <TabsContent value="library" className="mt-4">
                <TerminologyManager clientId={clientId} />
              </TabsContent>
              <TabsContent value="review" className="mt-4">
                <TermCandidateReviewPanel clientId={clientId} />
              </TabsContent>
            </Tabs>
          ) : (
            <TerminologyManager clientId={clientId} />
          )}
        </TabsContent>

        <TabsContent value="tm" className="mt-4">
          {client.enableTM ? (
            <TMManager clientId={clientId} />
          ) : (
            <Card>
              <CardContent className="pt-6 text-center text-sm text-muted-foreground">
                <Database className="h-8 w-8 mx-auto mb-2 opacity-50" />
                翻译记忆库未启用，请在顶部 TM 卡片中开启开关后再使用导入功能。
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="feedback" className="mt-4">
          <FeedbackList clientId={clientId} />
        </TabsContent>
      </Tabs>
    </div>
  )
}
